import { useState, useEffect, useMemo } from 'react'
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, ReferenceArea } from 'recharts'
import { Loader } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'
import { resolveIanaTz, wallClockPartsInTz, parseWallClockInTzToMs } from '../lib/tz'
import { ChartHoverTooltip } from './dashboard/ChartHoverTooltip'
import { readOverviewCombine, onPrimed, primeProject } from '../lib/widgetConfigStore'

const COLORS = [
  '#8b5cf6', // purple
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#06b6d4', // cyan
]

// Border colors from ValidateTab STATUS_CONFIG — slightly darker than bg, legible on white
const STATUS_DOT_COLOR = {
  outlier:    '#F1D862',
  suspect:    '#E89A57',
  erroneous:  '#E3776E',
  reviewed:   '#BD93CC',
  verified:   '#7DC57A',
  corrected:  '#7AAFE0',
  backfilled: '#BDBDBD',
}

// Plots raw (un-aggregated) samples for every D/W/M/Y view. The Validate tab
// requires that the chart and the spreadsheet share the EXACT same dataset —
// every 10-minute reading is rendered, no averaging, no bucketing.
// Only the x-axis tick formatting changes per period.
//
// `marks` (cellKey → statusId) and `hiddenStatuses` (Set<statusId>) let the
// Validate tab drive show/hide off the toolbar pills: any point whose cell is
// marked with a hidden status is dropped (y=null). When any status is hidden the
// line is drawn with connectNulls=false so it BREAKS at the hidden section (no
// segment is drawn across hidden data); with nothing hidden, connectNulls bridges
// genuine data gaps as before.
const pad2 = (n) => String(n).padStart(2, '0')
// Build the label for a UTC-ms timestamp in the REPORT's timezone, shifting by
// the account tz offset and reading UTC parts — byte-for-byte the same as
// useVisualizeData.formatTs, so the keys line up with `marks` (which the grid
// keys off that label). Using the browser's local time here (the old bug) made
// the keys never match, so marked points never coloured on the chart.
const labelForMsTz = (ms, offsetSeconds = 0) => {
  const d = new Date(ms + offsetSeconds * 1000)
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`
}

export function DynamicGraph({
  selectedParameters = [],
  dateRange = {},
  timezone = 'UTC',
  timePeriod = 'D',
  marks = null,
  hiddenStatuses = null,
  // Bumped by the parent after a server-side mutation (Validate-tab inline
  // edits, Edit-Value modal) so the chart re-pulls `Data.value` and reflects
  // the correction; the other deps don't change on those events.
  refreshNonce = 0,
  // When provided, drag-selection on the chart does NOT zoom the chart —
  // instead it emits { from, to } (epoch ms) to the parent so the parent
  // can filter a downstream view (e.g. the Validate-tab spreadsheet).
  // Pass `null` from the parent to clear the selection; a persistent
  // ReferenceArea highlights the current selection while it is active.
  onRangeSelect = null,
  selectedRange = null,
  // Project id so the Validate graph can inherit the Overview Line Chart's
  // Y-Axis combine, same as the Visualize chart (see readOverviewCombine).
  projectId = null,
  // Fetch ALL readings (bypass the per-project status-visibility filter) so the
  // Validate graph has the full dataset and the Show/Hide pills can toggle
  // points reactively via `hiddenStatuses`. Dashboard graphs leave this false
  // so the server-side rule removes hidden-status readings.
  includeHidden = false,
}) {
  const customerTz = useMemo(() => resolveIanaTz(timezone), [timezone])
  // Resolve projectId bullet-proof: prop first, else parse from the URL
  // (/dashboard/project/<id>) — useParams can come back empty in this subtree.
  const effProjectId = (projectId != null && projectId !== '')
    ? projectId
    : ((typeof window !== 'undefined' && window.location.pathname.match(/\/project\/(\d+)/)?.[1]) || null)
  // Prime the server-synced widget-config cache (idempotent) and re-render when
  // it lands, so the Overview combine carries over even on first paint.
  const [, setPrimeTick] = useState(0)
  useEffect(() => {
    if (effProjectId) primeProject(effProjectId)
    const off = onPrimed(() => setPrimeTick((t) => t + 1))
    return off
  }, [effProjectId])

  const [loading, setLoading] = useState(false)
  const [graphData, setGraphData] = useState([])
  // Account tz offset (seconds) from the graph-data response — used to build
  // report-tz labels that match the grid's `marks` keys.
  const [tzOffsetSec, setTzOffsetSec] = useState(0)
  const [seriesMetadata, setSeriesMetadata] = useState({})
  const [error, setError] = useState(null)
  // Drag-to-zoom — same UX as LineChartWidget on the Overview tab.
  const [zoomDragStart, setZoomDragStart] = useState(null)
  const [zoomDragEnd,   setZoomDragEnd]   = useState(null)
  const [zoomDomain,    setZoomDomain]    = useState(null)
  // Mobile layout flag — on a phone the multi-axis chart is cramped, so we thin
  // the x-axis ticks and slim the y-axes (visual only; no behaviour change).
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(max-width: 767px)')
    const onChange = (e) => setIsMobile(e.matches)
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange)
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange) }
  }, [])

  // Legend click-to-toggle: parameter ids the user has hidden. Clicking a
  // legend entry drops that series (its line AND its Y-axis) from the plot,
  // struck-through/greyed in the legend so it can be clicked to restore it —
  // matching the Overview line chart.
  const [hiddenParamIds, setHiddenParamIds] = useState(() => new Set())
  const toggleParamHidden = (id) => setHiddenParamIds((prev) => {
    const next = new Set(prev)
    const key = String(id)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })

  useEffect(() => {
    if (!selectedParameters || selectedParameters.length === 0) {
      setGraphData([])
      setSeriesMetadata({})
      return
    }

    let cancelled = false
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        const allData = {}
        const metadata = {}
        let capturedOffset = 0

        for (const param of selectedParameters) {
          if (!param.id) continue
          const paramId = Number(param.id)
          try {
            // No interval/agg → backend returns raw points.
            const response = await projectsApi.fetchGraphData(paramId, {
              fromDate: dateRange.from,
              toDate: dateRange.to,
              timezone: customerTz,
              includeHidden,
            })

            if (!response || typeof response === 'string') continue
            if (response.error === true) continue
            if (!Array.isArray(response.data)) continue

            metadata[param.id] = {
              name: param.name,
              unit: response.unit || param.unit || '',
              precision: response.precision ?? 2,
              latestValue: response.latestValue,
              latestTimestamp: response.latestTimestamp,
            }
            if (response.tzOffsetSeconds != null) capturedOffset = response.tzOffsetSeconds

            response.data.forEach((point) => {
              if (point.x == null || point.y == null) return
              const xNum = typeof point.x === 'string' ? parseInt(point.x, 10) : point.x
              if (isNaN(xNum)) return
              if (!allData[xNum]) allData[xNum] = { x: xNum }
              allData[xNum][`p_${param.id}`] = point.y
            })
          } catch (err) {
            console.error(`[DynamicGraph] fetch failed for param ${param.id}:`, err.message)
          }
        }

        if (cancelled) return

        const processed = Object.values(allData)
          .filter(d => Object.keys(d).some(k => k !== 'x' && d[k] != null))
          .sort((a, b) => a.x - b.x)

        setGraphData(processed)
        setSeriesMetadata(metadata)
        setTzOffsetSec(capturedOffset)
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load graph data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [selectedParameters, dateRange.from, dateRange.to, customerTz, refreshNonce, includeHidden])

  // Clear any drag-to-zoom selection when the dataset changes — switching
  // period / parameters / range should reset the view.
  useEffect(() => {
    setZoomDomain(null); setZoomDragStart(null); setZoomDragEnd(null)
  }, [selectedParameters, dateRange.from, dateRange.to, timePeriod])

  // Apply the toolbar's show/hide pills to the trace: any sample whose mark is
  // in `hiddenStatuses` is replaced by `null` for that parameter. Crucially we
  // KEEP every point (same x positions) rather than dropping fully-hidden ones —
  // a dropped point leaves no gap, so Recharts would connect its neighbours
  // straight across the hidden section. Keeping it as null preserves the gap so
  // the line BREAKS there (see connectNulls={false} on the <Line> when hiding).
  const filteredGraphData = useMemo(() => {
    if (!marks || !hiddenStatuses || hiddenStatuses.size === 0) return graphData
    return graphData.map((point) => {
      const label = labelForMsTz(point.x, tzOffsetSec)
      const next = { x: point.x }
      selectedParameters.forEach((param) => {
        const key = `p_${param.id}`
        if (!(key in point)) return
        const status = marks[`${param.id}|${label}`]
        next[key] = (status && hiddenStatuses.has(status)) ? null : point[key]
      })
      return next
    })
  }, [graphData, marks, hiddenStatuses, selectedParameters, tzOffsetSec])

  // Only render chart elements for parameters that actually have data in the
  // current filtered dataset. Empty parameters produce phantom Y-axes and
  // legend entries but no visible line.
  const parametersWithData = useMemo(() =>
    selectedParameters.filter(param =>
      filteredGraphData.some(row => row[`p_${param.id}`] != null)
    ),
  [selectedParameters, filteredGraphData])

  // Y-axis grouping — inherit the Overview Line Chart's "Y-Axis combine": any
  // parameters the user put on the same group there share ONE axis here too.
  // With no manual override, parameters are grouped by display-unit NAME (matches
  // the legacy Symfony V2 report graph), so every series in the same unit — e.g.
  // all °C temperatures regardless of parameter — shares ONE axis. Each group
  // draws a single YAxis; its member Lines point at it.
  const overviewCombine = readOverviewCombine(effProjectId)
  const axisGroups = useMemo(() => {
    const groups = []
    const byKey = new Map()
    parametersWithData.forEach((param, idx) => {
      const override = String(overviewCombine[String(param.id)] ?? '').trim()
      // Manual override wins (`grp:<n>`). Otherwise group by unit name
      // (`unit:<unit>`). Blank-unit series keep their own axis (`p:<id>`).
      const meta = seriesMetadata[param.id] || {}
      const unit = String(meta.unit ?? param.unit ?? '').trim().toLowerCase()
      const key = (override && override.toLowerCase() !== 'auto')
        ? `grp:${override.toLowerCase()}`
        : (unit ? `unit:${unit}` : `p:${param.id}`)
      let g = byKey.get(key)
      if (!g) { g = { key, axisId: `axis-${key}`, members: [], firstIdx: idx }; byKey.set(key, g); groups.push(g) }
      g.members.push({ param, idx })
    })
    return groups
  }, [parametersWithData, overviewCombine, seriesMetadata])
  // parameter id → the axis it belongs to.
  const axisIdForParam = useMemo(() => {
    const m = new Map()
    for (const g of axisGroups) for (const { param } of g.members) m.set(String(param.id), g.axisId)
    return m
  }, [axisGroups])
  const firstAxisId = axisGroups[0]?.axisId ?? ''

  // Tick format depends on the rolling window length, not on the data — so the
  // axis is always readable even when (e.g.) a month view contains 4000+ raw
  // points.
  //   D : "HH:MM"     W : "Mon 12"    M : "12 May"    Y : "May 25"
  const formatXAxisTick = (value) => {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return ''
    // W view: 12-hour ticks — midnight shows the date ("Jul 30"), noon "12:00".
    if (timePeriod === 'W') {
      const p = wallClockPartsInTz(d, customerTz)
      if (p.hour === 0 && p.minute === 0) {
        return new Intl.DateTimeFormat('en-US', { timeZone: customerTz, month: 'short', day: 'numeric' }).format(d)
      }
      return new Intl.DateTimeFormat('en-GB', { timeZone: customerTz, hour: '2-digit', minute: '2-digit', hour12: false }).format(d)
    }
    // M view: date labels ("Jul 8") at day boundaries, ~2-day spaced (mTicks).
    if (timePeriod === 'M') {
      return new Intl.DateTimeFormat('en-US', { timeZone: customerTz, month: 'short', day: 'numeric' }).format(d)
    }
    const opts = (() => {
      switch (timePeriod) {
        case 'Y': return { month: 'short', day: '2-digit', timeZone: customerTz }
        case 'D':
        default:  return { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: customerTz }
      }
    })()
    return new Intl.DateTimeFormat('en-GB', opts).format(d)
  }

  // D view: even 2-hour tick marks anchored to even-hour boundaries IN THE
  // CUSTOMER'S TZ across the plotted window — so the axis reads
  // "02:00 · 04:00 · 06:00 · … · 00:00" like the Overview chart. Without this,
  // Recharts auto-generates time ticks from the raw data and the axis comes out
  // sparse/uneven (e.g. "03:00 05:00 07:00 … 23:00 01:00").
  const dTicks = useMemo(() => {
    if (timePeriod !== 'D' || graphData.length === 0) return undefined
    const xs = graphData.map((pt) => pt.x)
    const startMs = Math.min(...xs)
    const endMs   = Math.max(...xs)
    const sp = wallClockPartsInTz(new Date(startMs), customerTz)
    // First even-hour boundary at or after the window start.
    const roundUpHour = sp.minute === 0 && sp.second === 0
      ? (sp.hour % 2 === 0 ? sp.hour : sp.hour + 1)
      : sp.hour + (sp.hour % 2 === 0 ? 2 : 1)
    const firstStr =
      `${sp.year}-${String(sp.month).padStart(2, '0')}-${String(sp.day).padStart(2, '0')} ` +
      `${String(roundUpHour % 24).padStart(2, '0')}:00:00`
    const dayAdvance = Math.floor(roundUpHour / 24)
    let firstMs = parseWallClockInTzToMs(firstStr, customerTz)
    if (firstMs == null) return undefined
    firstMs += dayAdvance * 24 * 60 * 60 * 1000
    const ticks = []
    for (let t = firstMs; t <= endMs; t += 2 * 60 * 60 * 1000) ticks.push(t)
    return ticks
  }, [timePeriod, graphData, customerTz])

  // W view: 12-hour ticks at tz-local midnight & noon across the data window.
  const wTicks = useMemo(() => {
    if (timePeriod !== 'W' || graphData.length === 0) return undefined
    const xs = graphData.map((pt) => pt.x)
    const startMs = Math.min(...xs)
    const endMs   = Math.max(...xs)
    const sp = wallClockPartsInTz(new Date(startMs), customerTz)
    const onBoundary = sp.minute === 0 && sp.second === 0 && (sp.hour === 0 || sp.hour === 12)
    const nextHour = onBoundary ? sp.hour : (sp.hour < 12 ? 12 : 24)
    const dayAdvance = Math.floor(nextHour / 24)
    const firstStr =
      `${sp.year}-${String(sp.month).padStart(2, '0')}-${String(sp.day).padStart(2, '0')} ` +
      `${String(nextHour % 24).padStart(2, '0')}:00:00`
    let firstMs = parseWallClockInTzToMs(firstStr, customerTz)
    if (firstMs == null) return undefined
    firstMs += dayAdvance * 24 * 60 * 60 * 1000
    const t2 = []
    for (let t = firstMs; t <= endMs; t += 12 * 60 * 60 * 1000) t2.push(t)
    return t2
  }, [timePeriod, graphData, customerTz])

  // M view: day-boundary ticks at a "nice" day-step (~15 labels).
  const mTicks = useMemo(() => {
    if (timePeriod !== 'M' || graphData.length === 0) return undefined
    const xs = graphData.map((pt) => pt.x)
    const startMs = Math.min(...xs)
    const endMs   = Math.max(...xs)
    const totalDays = Math.max(1, Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)))
    const NICE = [1, 2, 3, 5, 7, 10, 14]
    const step = NICE.find((n) => n >= totalDays / 15) || 14
    const sp = wallClockPartsInTz(new Date(startMs), customerTz)
    const atMidnight = sp.hour === 0 && sp.minute === 0 && sp.second === 0
    const firstStr = `${sp.year}-${String(sp.month).padStart(2,'0')}-${String(sp.day).padStart(2,'0')} 00:00:00`
    let firstMs = parseWallClockInTzToMs(firstStr, customerTz)
    if (firstMs == null) return undefined
    if (!atMidnight) firstMs += 24 * 60 * 60 * 1000
    const out = []
    for (let t = firstMs; t <= endMs; t += step * 24 * 60 * 60 * 1000) out.push(t)
    return out
  }, [timePeriod, graphData, customerTz])

  const formatTooltipLabel = (value) => {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return ''
    return new Intl.DateTimeFormat('en-US', {
      timeZone: customerTz,
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(d)
  }

  if (loading) {
    return (
      <div className="w-full h-96 flex items-center justify-center bg-gray-50 rounded border border-gray-200">
        <div className="flex flex-col items-center gap-2">
          <Loader className="w-6 h-6 text-blue-500 animate-spin" />
          <p className="text-sm text-gray-600">Loading graph data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full h-96 flex items-center justify-center bg-red-50 rounded border border-red-200">
        <div className="text-center max-w-md">
          <p className="text-red-600 text-sm font-semibold mb-2">Error loading graph data</p>
          <p className="text-red-500 text-xs">{error}</p>
        </div>
      </div>
    )
  }

  const hasData = filteredGraphData.length > 0

  // Each parameter gets its OWN hidden y-axis so wildly different scales
  // (heading 0–360, air-temp ~20, rain mm, outliers ~-100k) don't collapse
  // the smaller series into a flat line at the bottom. Only the first
  // parameter's axis is visible; the rest are hidden but still active for
  // scaling their respective Line. Gridlines use a slightly darker stroke
  // in M/Y so the long sweep stays readable.
  const gridStroke = (timePeriod === 'M' || timePeriod === 'Y') ? '#c7d2da' : '#e5e7eb'

  return (
    <div className="w-full bg-white rounded border border-gray-200 p-4">
      <div className="relative select-none">
        {hasData ? (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={filteredGraphData}
              margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
              // Drag-to-zoom — same shape as the Overview LineChartWidget.
              onMouseDown={(e) => {
                if (e?.activeLabel == null) return
                setZoomDragStart(e.activeLabel); setZoomDragEnd(e.activeLabel)
              }}
              onMouseMove={(e) => {
                if (zoomDragStart == null || e?.activeLabel == null) return
                setZoomDragEnd(e.activeLabel)
              }}
              onMouseUp={() => {
                if (zoomDragStart == null || zoomDragEnd == null) { setZoomDragStart(null); setZoomDragEnd(null); return }
                if (zoomDragStart === zoomDragEnd) { setZoomDragStart(null); setZoomDragEnd(null); return }
                const lo = (typeof zoomDragStart === 'number' && typeof zoomDragEnd === 'number')
                  ? Math.min(zoomDragStart, zoomDragEnd) : zoomDragStart
                const hi = (typeof zoomDragStart === 'number' && typeof zoomDragEnd === 'number')
                  ? Math.max(zoomDragStart, zoomDragEnd) : zoomDragEnd
                // Two modes:
                //   • Parent passed onRangeSelect → emit the range and DO
                //     NOT crop the chart. Used by ValidateTab so dragging
                //     filters the spreadsheet below without changing the
                //     visible chart domain.
                //   • Otherwise → classic drag-to-zoom behaviour.
                if (typeof onRangeSelect === 'function') {
                  onRangeSelect({ from: lo, to: hi })
                } else {
                  setZoomDomain([lo, hi])
                }
                setZoomDragStart(null); setZoomDragEnd(null)
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis
                dataKey="x"
                type="number"
                scale="time"
                domain={zoomDomain || ['dataMin', 'dataMax']}
                allowDataOverflow={!!zoomDomain}
                tickFormatter={formatXAxisTick}
                tick={{ fontSize: isMobile ? 10 : 11, fill: '#888' }}
                {...(() => {
                  const raw = dTicks || wTicks || mTicks
                  if (raw && !zoomDomain) {
                    // On a phone, thin the explicit ticks to ~7 evenly-spaced
                    // labels so the time axis doesn't overlap (matches the XD);
                    // desktop keeps every tick.
                    const ticks = isMobile && raw.length > 7
                      ? raw.filter((_, i) => i % Math.ceil(raw.length / 7) === 0)
                      : raw
                    return { ticks, interval: 0, minTickGap: 0 }
                  }
                  return { minTickGap: isMobile ? 50 : 40 }
                })()}
              />
              {/* Per-parameter Y axes — each visible and tinted to match
                  its line, alternating left/right so multiple parameters
                  with different units (°C / m / mg/L / etc.) each get a
                  legible scale. Mirrors the Overview chart's behaviour. */}
              {axisGroups.map((group, gIdx) => {
                const first     = group.members[0]
                const meta      = seriesMetadata[first.param.id] || {}
                const shared    = group.members.length > 1
                // A shared axis carries lines of different colours, so tint it
                // neutrally; a solo axis keeps its line's colour.
                const axisColor = shared ? '#6B7280' : COLORS[first.idx % COLORS.length]
                const side      = gIdx % 2 === 0 ? 'left' : 'right'
                const allHidden = group.members.every((m) => hiddenParamIds.has(String(m.param.id)))
                return (
                  <YAxis
                    key={group.axisId}
                    yAxisId={group.axisId}
                    hide={allHidden}
                    orientation={side}
                    tick={{ fontSize: isMobile ? 9 : 11, fill: axisColor }}
                    tickLine={{ stroke: axisColor }}
                    axisLine={{ stroke: axisColor }}
                    width={isMobile ? 40 : 55}
                    domain={['auto', 'auto']}
                    label={meta.unit ? {
                      value: meta.unit,
                      angle: -90,
                      position: side === 'left' ? 'insideLeft' : 'insideRight',
                      style: { fontSize: isMobile ? 9 : 10, fill: axisColor, fontWeight: 600 },
                    } : undefined}
                  />
                )
              })}
              <Tooltip
                isAnimationActive={false}
                wrapperStyle={{ transition: 'none' }}
                cursor={{ stroke: '#9ca3af', strokeWidth: 1, strokeDasharray: '4 4' }}
                content={(p) => (
                  <ChartHoverTooltip
                    {...p}
                    formatLabel={formatTooltipLabel}
                    describe={(entry) => {
                      const id = String(entry?.dataKey || '').replace('p_', '')
                      const m = seriesMetadata[id] || {}
                      const v = typeof entry?.value === 'number' ? entry.value.toFixed(m.precision ?? 2) : (entry?.value ?? '-')
                      return {
                        name: m.name || id,
                        valueText: `${v}${m.unit ? ` ${m.unit}` : ''}`,
                        color: entry?.color || entry?.stroke,
                      }
                    }}
                  />
                )}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8, cursor: 'pointer' }}
                formatter={(key, entry) => {
                  const id = String(entry?.dataKey || key).replace('p_', '')
                  const name = seriesMetadata[id]?.name || key
                  const hidden = hiddenParamIds.has(id)
                  return (
                    <span style={hidden ? { textDecoration: 'line-through', color: '#9ca3af' } : undefined}>
                      {name}
                    </span>
                  )
                }}
                onClick={(o) => {
                  const dk = o?.dataKey || o?.value
                  if (dk) toggleParamHidden(String(dk).replace('p_', ''))
                }}
              />
              {parametersWithData.map((param, idx) => {
                // No per-point dots on the Validate chart: marked readings used
                // to be drawn as status-colored circles, but that cluttered the
                // line — marks are managed in the grid/table instead, so the
                // chart stays a clean line.
                return (
                  <Line
                    key={param.id}
                    yAxisId={axisIdForParam.get(String(param.id)) || firstAxisId}
                    hide={hiddenParamIds.has(String(param.id))}
                    type="linear"
                    dataKey={`p_${param.id}`}
                    stroke={COLORS[idx % COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                    // When a status is being hidden (Validate), the hidden
                    // readings are nulled out — break the line at those gaps so
                    // no segment is drawn across the hidden section. With nothing
                    // hidden (dashboard / all-shown), bridge genuine gaps as before.
                    connectNulls={!(hiddenStatuses && hiddenStatuses.size > 0)}
                    name={`p_${param.id}`}
                  />
                )
              })}
              {zoomDragStart != null && zoomDragEnd != null && zoomDragStart !== zoomDragEnd && (
                <ReferenceArea
                  yAxisId={firstAxisId}
                  x1={zoomDragStart}
                  x2={zoomDragEnd}
                  strokeOpacity={0.3}
                  fill="#358ECD"
                  fillOpacity={0.15}
                />
              )}
              {/* Persistent parent-controlled selection (e.g. Validate
                  tab range filter) — shaded band stays until parent
                  clears it. Rendered only when caller opts into
                  onRangeSelect mode. */}
              {selectedRange && selectedRange.from != null && selectedRange.to != null && (
                <ReferenceArea
                  yAxisId={firstAxisId}
                  x1={selectedRange.from}
                  x2={selectedRange.to}
                  strokeOpacity={0.4}
                  stroke="#358ECD"
                  fill="#358ECD"
                  fillOpacity={0.12}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={[]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="x" type="number" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}

        {zoomDomain && hasData && (
          <button
            type="button"
            onClick={() => { setZoomDomain(null); setZoomDragStart(null); setZoomDragEnd(null) }}
            className="absolute top-2 right-2 px-2.5 py-1 text-xs font-semibold text-[#358ECD] bg-white border border-[#358ECD] rounded shadow-sm hover:bg-[#E8F1F8] transition z-10"
          >
            Reset Zoom
          </button>
        )}

        {!hasData && selectedParameters.length > 0 && !loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded">
            <p className="text-gray-500 text-sm">No data available for the selected time period</p>
          </div>
        )}

        {selectedParameters.length === 0 && !loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded">
            <p className="text-gray-500 text-sm">Select parameters to display graph</p>
          </div>
        )}
      </div>
    </div>
  )
}
